import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'

import { Col, Row } from 'antd'
//
import global from 'common'

import { CreditCardDetails } from 'core/application/adminDashboard/companySettings/subscription/subscriptionDetails'
import updatePaymentInformation from 'core/application/adminDashboard/companySettings/subscription/upatePaymentInformation'

import notification from 'common/components/feedback/Notification'
import Button from 'common/components/general/Button'

import { commandRequest } from 'common/RequestUtils'
import { getCreditCardToken } from 'common/recultyUtils'

import SubscriptionDetail from '../../components/SubscriptionDetail'
import CreditCardForm from './CreditCardForm'

interface CreditCardProps extends CreditCardDetails {
  onSave: () => void
}

const CreditCard: React.FC<CreditCardProps> = ({
  cardNumberLastFour,
  firstName,
  lastName,
  expirationMonth,
  expirationYear,
  onSave,
}) => {
  const { t: translate } = useTranslation()
  const { isXs } = global.hooks.useBreakPoints()

  const [editMode, setEditMode] = useState<boolean>(false)
  const [creditCardRecurlyErrors, setCreditCardRecurlyErrors] = useState<Map<string, string>>(new Map())
  const [loading, setLoading] = useState<boolean>(false)

  const onSaveClick = () => {
    handleCreditCardPayment()
  }

  const handleCreditCardPayment = async () => {
    return new Promise<void>(async (resolve, reject) => {
      setLoading(true)
      try {
        const token = await getCreditCardToken()

        const result = await commandRequest(() =>
          updatePaymentInformation({
            recurlyToken: token,
          }),
        )

        if (result.errors) {
          notification.open({
            message: translate('admin-signup.subscription.errors.went-wrong-message'),
            description: translate('admin-signup.subscription.errors.connection-error'),
            type: 'error',
          })
          setLoading(false)
          return
        }
        setEditMode(false)
        onSave()
        resolve()
      } catch (errors) {
        setCreditCardRecurlyErrors(errors)
        setLoading(false)

        reject()
      }
    })
  }

  return editMode ? (
    <div>
      <CreditCardForm
        recurlyErrors={creditCardRecurlyErrors}
        onSaveClick={onSaveClick}
        onCancel={() => {
          setEditMode(false)
          setCreditCardRecurlyErrors(new Map())
        }}
        firstName={firstName}
        lastName={lastName}
      />
    </div>
  ) : (
    <Row>
      <Col xs={24} sm={20}>
        <div>
          <SubscriptionDetail
            title={translate('admin-dashboard.company-settings.subscription.payment-method')}
            description={translate('admin-dashboard.company-settings.subscription.credit-card')}
          />
        </div>
        <Row gutter={24}>
          <Col span={8}>
            <SubscriptionDetail
              title={translate('admin-dashboard.company-settings.subscription.cardholder-name')}
              description={`${firstName} ${lastName}`}
            />
          </Col>
          <Col span={8}>
            <SubscriptionDetail
              title={translate('admin-dashboard.company-settings.subscription.card-number')}
              description={`${cardNumberLastFour}`}
            />
          </Col>
          <Col span={8}>
            <SubscriptionDetail
              title={translate('admin-dashboard.company-settings.subscription.expiration-date')}
              description={`${expirationMonth}/${expirationYear}`}
            />
          </Col>
        </Row>
      </Col>
      <Col xs={24} sm={4} className={`flex ${isXs ? 'mb-16' : 'justify-end align-end mb-16'}`}>
        <Button
          type="primary"
          icon={'far fa-pen'}
          onClick={() => {
            setEditMode(!editMode)
          }}
        >
          {translate('general.edit')}
        </Button>
      </Col>
    </Row>
  )
}

export default CreditCard
